/**
 * PP Mobile Drawer — lazy-loaded vía Section Rendering API.
 *
 * Menú principal mobile (navegación por niveles con back). Antes se renderizaba
 * server-side en el layout de CADA página (~224 thumbnails). Ahora el layout deja
 * solo un contenedor vacío (#PpMobileDrawerMount); este JS trae la section
 * `pp-mobile-drawer` on-demand (prefetch en idle y al tocar la hamburguesa),
 * la inyecta una vez y engancha la navegación.
 *
 * Fallback: si el fetch falla, la hamburguesa navega a /collections (el menú es
 * la navegación principal en mobile, no puede quedar muerto).
 */
(function () {
  class PpMobileDrawer {
    constructor() {
      this.mount = document.getElementById('PpMobileDrawerMount');
      this.triggers = Array.from(document.querySelectorAll('[data-pp-mobile-drawer-trigger]'));
      if (!this.mount || !this.triggers.length) return;

      this.drawer = null;
      this.loaded = false;
      this._loading = null;
      // Stack de breadcrumbs; el nivel "0" siempre está en el fondo.
      this.stack = [{ levelId: '0', title: 'Menú', fromLabel: 'Regresar' }];

      this.bindTriggers();
    }

    bindTriggers() {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.openFrom(trigger);
        });
        // Prefetch al primer contacto táctil, antes de soltar el tap.
        trigger.addEventListener('touchstart', () => this.load(), { passive: true });
      });

      // Prefetch en reposo: que el primer tap encuentre el drawer ya listo.
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.load(), { timeout: 3000 });
      } else {
        window.setTimeout(() => this.load(), 2000);
      }
    }

    /** Trae e inyecta la section una sola vez. Idempotente; devuelve una promesa. */
    load() {
      if (this.loaded) return Promise.resolve();
      if (this._loading) return this._loading;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this._loading = fetch(`${root}?section_id=pp-mobile-drawer`)
        .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
        .then((html) => {
          const tmp = document.createElement('template');
          tmp.innerHTML = html;
          const drawer = tmp.content.querySelector('#PpMobileDrawer');
          if (!drawer) return;
          this.mount.appendChild(drawer);
          this.setup();
          this.loaded = true;
        })
        .catch(() => {
          this._loading = null;
        });

      return this._loading;
    }

    /** Cachea referencias del contenido inyectado y engancha eventos. */
    setup() {
      this.drawer = document.getElementById('PpMobileDrawer');
      this.panels = Array.from(this.drawer.querySelectorAll('[data-pp-md-level]'));
      this.title = this.drawer.querySelector('[data-pp-md-title]');
      this.backBtn = this.drawer.querySelector('[data-pp-md-back]');
      this.backLabel = this.drawer.querySelector('[data-pp-md-back-label]');
      this.closers = Array.from(this.drawer.querySelectorAll('[data-pp-md-close]'));
      this.panel = this.drawer.querySelector('[data-pp-md-panel]');
      this.bindContent();
    }

    bindContent() {
      this.closers.forEach((closer) => {
        closer.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      this.backBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this.goBack();
      });

      this.drawer.querySelectorAll('[data-pp-md-navigate]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const levelId = btn.getAttribute('data-pp-md-navigate');
          const title = btn.getAttribute('data-pp-md-navigate-title') || 'Menú';
          this.navigateTo(levelId, title);
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          if (this.stack.length > 1) {
            this.goBack();
          } else {
            this.close();
          }
        }
      });

      document.addEventListener('pointerdown', (e) => {
        if (!this.isOpen || !this.panel) return;
        if (this.panel.contains(e.target)) return;
        if (e.target.closest('[data-pp-mobile-drawer-trigger]')) return;
        this.close();
      });
    }

    async openFrom(triggerEl) {
      this.triggerEl = triggerEl;
      await this.load();
      if (this.drawer) {
        this.open();
      } else {
        // El fetch falló → no dejamos al usuario sin navegación.
        window.location.href = '/collections';
      }
    }

    get isOpen() {
      return !!this.drawer && this.drawer.classList.contains('is-open');
    }

    open() {
      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pp-mobile-drawer-open');
      this.triggerEl?.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
      });

      this.resetToRoot();
    }

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.triggerEl?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('pp-mobile-drawer-open');

      window.setTimeout(() => {
        this.drawer.hidden = true;
        this.drawer.setAttribute('aria-hidden', 'true');
        this.resetToRoot();
      }, 300);
    }

    resetToRoot() {
      this.stack = [{ levelId: '0', title: 'Menú', fromLabel: 'Regresar' }];
      this.applyStack();
    }

    navigateTo(levelId, title) {
      const targetPanel = this.drawer.querySelector(`[data-pp-md-level="${CSS.escape(levelId)}"]`);
      if (!targetPanel) return;
      const currentTop = this.stack[this.stack.length - 1];
      this.stack.push({ levelId, title, fromLabel: currentTop.title });
      this.applyStack();
    }

    goBack() {
      if (this.stack.length <= 1) return;
      this.stack.pop();
      this.applyStack();
    }

    applyStack() {
      const top = this.stack[this.stack.length - 1];
      const prevTop = this.stack[this.stack.length - 2];
      const stackIds = new Set(this.stack.map((s) => s.levelId));

      this.panels.forEach((panel) => {
        const levelId = panel.getAttribute('data-pp-md-level');
        panel.classList.remove('is-active', 'is-prev');
        panel.hidden = false;
        if (levelId === top.levelId) {
          panel.classList.add('is-active');
        } else if (stackIds.has(levelId)) {
          panel.classList.add('is-prev');
        }
      });

      if (this.title) this.title.textContent = top.title;

      if (this.backBtn) {
        if (this.stack.length > 1) {
          this.backBtn.hidden = false;
          if (this.backLabel) this.backLabel.textContent = prevTop.title || 'Regresar';
        } else {
          this.backBtn.hidden = true;
        }
      }
    }
  }

  function init() {
    new PpMobileDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
